import React, { useState, useImperativeHandle, forwardRef, useEffect } from 'react';
import "./notification.scss";
import { MaterialIcon } from 'component/MaterialSymbols';
import { BackgroundWithOrnaments } from 'component/Ornaments';

const NotificationLightbox = forwardRef((props, ref) => {
  const [visible, setVisible] = useState(false);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [icon, setIcon] = useState('');
  const [status, setStatus] = useState('preloader');
  const [timeoutId, setTimeoutId] = useState(null);
  const [currentCallbacks, setCurrentCallbacks] = useState(null);

  // Execute and clear callback
  const executeCallback = (name) => {
    if (!currentCallbacks) return;
    const callback = currentCallbacks[name];
    if (callback) {
      callback();
      setCurrentCallbacks(prev => ({
        ...prev,
        [name]: undefined  // Clear only this callback
      }));
    }
  };

  useEffect(() => {
    if (visible && currentCallbacks?.onRendered) {
      executeCallback('onRendered');
    }
    
    return () => {
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [visible]);

  const closeLightbox = () => {
    if (timeoutId) {
      clearTimeout(timeoutId);
      setTimeoutId(null);
    }
    setVisible(false);
    executeCallback('onDestroy');
  };

  useImperativeHandle(ref, () => ({
    show(options) {
      // Store new callbacks
      if (options._callbacks) {
        setCurrentCallbacks(options._callbacks);
      }

      if (timeoutId) {
        clearTimeout(timeoutId);
        setTimeoutId(null);
      }
      
      setTitle(options.title || '');
      setDescription(options.description || '');
      setIcon(options.icon || (
        options.status === 'preloader' ? 'hourglass_empty' :
        options.status === 'ok' ? 'done_all' :
        options.status === 'error' ? 'error' : ''
      ));
      setStatus(options.status || 'preloader');
      setVisible(true);
      
      if (options.autoCloseDuration) {
        const id = setTimeout(() => {
          setVisible(false);
          executeCallback('onDestroy');
        }, options.autoCloseDuration);
        setTimeoutId(id);
      }
    },
    
    update(options) {
      if (options.title !== undefined) setTitle(options.title);
      if (options.description !== undefined) setDescription(options.description);
      
      if (options.icon !== undefined) {
        setIcon(options.icon);
      } else if (options.status !== undefined) {
        setIcon(
          options.status === 'preloader' ? 'hourglass_empty' :
          options.status === 'ok' ? 'done_all' :
          options.status === 'error' ? 'error' : ''
        );
      }
      
      if (options.status !== undefined) setStatus(options.status);
    },
    
    hide() {
      if (timeoutId) {
        clearTimeout(timeoutId);
        setTimeoutId(null);
      }
      setVisible(false);
      executeCallback('onDestroy');
    }
  }));

  if (!visible) return null;

  return (
    <div className={`notification-lightbox ${status}`}>
      <div className='notification-backdrop'></div>
      <div className="notification-content">
        <div className="notification-close" onClick={closeLightbox}>&#x2715;</div>
        <div className="notification-title relative bg-ornaments--title">
          <BackgroundWithOrnaments><h3>{title}</h3></BackgroundWithOrnaments>
        </div>
        <div className="notification-description bg-ornaments--body">
          <MaterialIcon icon={icon} size={24} className="notification-icon" />
          <div className='relative text-sm' dangerouslySetInnerHTML={{ __html: description }} />
        </div>
      </div>
    </div>
  );
});

export default NotificationLightbox;